import React, { Dispatch } from 'react';
import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { classes } from './styles';
import { sharedClasses } from '../../../NewUI/Components/CustomUIElements/sharedClasses';
import { FormNumberField } from '../../../NewUI/Components/CustomUIElements/FormTextField';
import {
  ControlledTimePicker,
  SingleDatePicker
} from '../../../NewUI/Components/CustomUIElements/SingleDatePicker';
import { IEventType, IJob, ScoutCalendarAction, ScoutCalendarState } from '../../types';
import MultipleDatesPicker from '../../../NewUI/Components/CustomUIElements/MultipleDatesPicker';
import { VirtualizedMultiSelect } from '../../../NewUI/Components/CustomUIElements/VirtualizedMultiSelect';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
import { theme } from '../../../../components/ThemeContext/ThemeObject';
import { useFeatureFlag } from '../../../FeatureFlags';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

export default function FirstStep({
  eventTypes,
  loadingOptions,
  recruiterOptions,
  loadingRecruiterOptions,
  jobOptions,
  loadingJobOptions,
  ScoutCalendarState,
  dispatch,
  userTimezone
}: {
  eventTypes?: IEventType[];
  loadingOptions: boolean;
  recruiterOptions?: { id: number; name: string; checked: boolean }[];
  loadingRecruiterOptions: boolean;
  jobOptions?: IJob[];
  loadingJobOptions: boolean;
  ScoutCalendarState: ScoutCalendarState;
  dispatch: Dispatch<ScoutCalendarAction>;
  userTimezone: string;
}) {
  const newEventManagementEnabled = useFeatureFlag('newEventManagementEnabled');
  const { createEventObject, errorsMessage, createMultipleEvents } = ScoutCalendarState;
  const { eventDate } = createEventObject;

  const handleTimeChange = (time: Dayjs | null) => {
    if (!time) return;
    if (!time.isValid()) {
      dispatch({
        type: 'SET_ERROR_MESSAGE',
        payload: { ...errorsMessage, time: 'Please input a valid time' }
      });
    } else {
      dispatch({
        type: 'SET_ERROR_MESSAGE',
        payload: { ...errorsMessage, time: '' }
      });
      const hourAndMinute = time.format('H:mm').split(':');
      const newDates = createEventObject.dates.map((date: Date) => {
        if (createEventObject.eventId) {
          // For existing events, extract date in user's timezone and combine with new time
          const dateStr = dayjs(date).tz(userTimezone).format('YYYY-MM-DD');
          return dayjs
            .tz(`${dateStr} ${hourAndMinute[0]}:${hourAndMinute[1]}:00`, userTimezone)
            .toDate();
        } else {
          // For new events, work in user's timezone
          const dateStr = dayjs(date).tz(userTimezone).format('YYYY-MM-DD');
          const userTimezoneTime = dayjs.tz(
            `${dateStr} ${hourAndMinute[0]}:${hourAndMinute[1]}:00`,
            userTimezone
          );
          // Convert to UTC to preserve the exact moment in time
          return userTimezoneTime.toDate();
        }
      });
      dispatch({
        type: 'SET_CREATE_EVENT_OBJECT',
        payload: {
          ...createEventObject,
          eventDate: newDates[0],
          dates: newDates
        }
      });
    }
  };

  return (
    <Box>
      <Box sx={classes.progressHeader}>Interview date and attendees</Box>
      <Stack
        sx={{
          height: '455px',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          rowGap: 3
        }}
      >
        {createEventObject.eventId ? (
          <Stack sx={{ paddingLeft: 0.5, rowGap: 1 }}>
            <Stack sx={{ color: '#939393', flexDirection: 'row', columnGap: '2px' }}>
              Event type <Stack sx={{ color: theme.palette.error.main }}>*</Stack>
            </Stack>
            <Stack>{createEventObject.eventType?.name}</Stack>
          </Stack>
        ) : (
          <Autocomplete
            disablePortal
            autoHighlight
            includeInputInList
            options={eventTypes || []}
            groupBy={(option) => option.entity_name}
            loading={loadingOptions}
            getOptionLabel={(option) => option.name}
            noOptionsText="No event types"
            value={createEventObject.eventType}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, newValue) =>
              dispatch({
                type: 'SET_CREATE_EVENT_OBJECT',
                payload: {
                  ...createEventObject,
                  eventType: newValue,
                  duration: newValue ? String(newValue.duration) : '15',
                  maxCandidates: newValue ? String(newValue.max_candidates) : '1'
                }
              })
            }
            sx={{ ...sharedClasses.formAutocomplete, width: '100%', marginTop: 3 }}
            ListboxProps={{ style: classes.autocompleteListboxStyle }}
            renderInput={(params) => (
              <TextField
                {...params}
                id="event-type-select"
                sx={classes.autocompleteTextfield}
                label="Choose Event Type"
                InputLabelProps={{ shrink: true }}
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingOptions ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
                helperText={errorsMessage.eventType}
              />
            )}
          />
        )}
        <Stack sx={{ flexDirection: 'row', columnGap: 3, margin: '3px' }}>
          <Box sx={{ width: '100%' }}>
            {createMultipleEvents ? (
              <MultipleDatesPicker
                values={createEventObject.dates.map((date: Date) => dayjs(date).tz(userTimezone))}
                label={'Dates'}
                required
                minDate={dayjs()}
                timezone={userTimezone}
                onAccept={(dates) => {
                  const selectedTime = dayjs
                    .utc(eventDate)
                    .tz(userTimezone)
                    .format('H:mm')
                    .split(':');
                  const datesWithTime = dates.map((date: Date | Dayjs) => {
                    const dateStr = dayjs(date).tz(userTimezone).format('YYYY-MM-DD');
                    const userTimezoneTime = dayjs.tz(
                      `${dateStr} ${selectedTime[0]}:${selectedTime[1]}:00`,
                      userTimezone
                    );
                    return userTimezoneTime.toDate();
                  });
                  dispatch({
                    type: 'SET_CREATE_EVENT_OBJECT',
                    payload: {
                      ...createEventObject,
                      dates: datesWithTime,
                      eventDate: datesWithTime[0]
                    }
                  });
                }}
                error={errorsMessage.dates}
              />
            ) : (
              <>
                <SingleDatePicker
                  value={createEventObject.eventDate}
                  label={'Date'}
                  inputStyles={classes.datePicker}
                  disablePast
                  onChange={(value) => {
                    if (!value?.isValid()) {
                      dispatch({
                        type: 'SET_ERROR_MESSAGE',
                        payload: { ...errorsMessage, date: 'Please input a valid date' }
                      });
                    } else {
                      dispatch({
                        type: 'SET_ERROR_MESSAGE',
                        payload: { ...errorsMessage, date: '' }
                      });
                      const newDate = dayjs(value.toDate()).second(0).toDate();
                      value?.isValid() &&
                        dispatch({
                          type: 'SET_CREATE_EVENT_OBJECT',
                          payload: {
                            ...createEventObject,
                            eventDate: newDate,
                            dates: [newDate]
                          }
                        });
                    }
                  }}
                  showTodayButton
                  error={errorsMessage.date}
                />
              </>
            )}
          </Box>
          <Box sx={{ width: '100%' }} data-testid="time-picker-input-container">
            <ControlledTimePicker
              onChange={handleTimeChange}
              value={dayjs.utc(createEventObject.eventDate).tz(userTimezone)}
              label="Time"
              minutesInterval={1}
            />
            {!!errorsMessage.time && (
              <Typography variant="caption" sx={classes.errorMessage}>
                {errorsMessage.time}
              </Typography>
            )}
          </Box>
          <FormNumberField
            label={'Duration'}
            value={createEventObject.duration}
            onChange={(e) =>
              dispatch({
                type: 'SET_CREATE_EVENT_OBJECT',
                payload: { ...createEventObject, duration: e.target.value }
              })
            }
            unit="minutes"
            fullWidth
            error={errorsMessage.duration}
            id="duration-input"
          />
        </Stack>
        <Stack
          sx={{
            flexDirection: 'row',
            columnGap: 3,
            margin: '3px'
          }}
        >
          <FormNumberField
            label={'Max. candidates per session'}
            value={createEventObject.maxCandidates}
            onChange={(e) =>
              dispatch({
                type: 'SET_CREATE_EVENT_OBJECT',
                payload: { ...createEventObject, maxCandidates: e.target.value }
              })
            }
            styles={{ width: createMultipleEvents ? undefined : '31%' }}
            fullWidth
            required
            error={errorsMessage.maxCandidates}
            id="max-candidates-input"
          />
          {createMultipleEvents && (
            <>
              <FormNumberField
                label={'Sessions (in the day)'}
                value={createEventObject.sessions}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_CREATE_EVENT_OBJECT',
                    payload: { ...createEventObject, sessions: e.target.value }
                  })
                }
                fullWidth
                required
                error={errorsMessage.sessions}
              />
              <FormNumberField
                label={'Break (in between sessions)'}
                value={createEventObject.breaks}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_CREATE_EVENT_OBJECT',
                    payload: { ...createEventObject, breaks: e.target.value }
                  })
                }
                unit="minutes"
                fullWidth
                required
              />
            </>
          )}
        </Stack>
        <Box>
          <Autocomplete
            multiple
            options={recruiterOptions || []}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={createEventObject.recruiters}
            loading={loadingRecruiterOptions}
            onChange={(_e, newValue) =>
              dispatch({
                type: 'SET_CREATE_EVENT_OBJECT',
                payload: { ...createEventObject, recruiters: newValue }
              })
            }
            sx={{ ...sharedClasses.formAutocomplete, width: '100%', marginTop: 3 }}
            ListboxProps={{ style: classes.autocompleteListboxStyle }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add recruiters"
                sx={classes.autocompleteTextfield}
                InputLabelProps={{ shrink: true }}
                placeholder={createEventObject.recruiters.length ? '' : 'Search Users'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingRecruiterOptions ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
                helperText={errorsMessage.recruiters}
              />
            )}
          />
        </Box>
        {newEventManagementEnabled && (
          <Box>
            <VirtualizedMultiSelect
              options={jobOptions || []}
              value={createEventObject.jobs}
              onChange={(jobs) =>
                dispatch({
                  type: 'SET_CREATE_EVENT_OBJECT',
                  payload: { ...createEventObject, jobs }
                })
              }
              styles={{
                ...sharedClasses.formAutocomplete,
                width: '100%',
                marginTop: 3,
                paddingRight: '8px'
              }}
              loading={loadingJobOptions}
              loadingText="Loading jobs..."
              getOptionLabel={(option: IJob) => `${option.reference} - ${option.title}`}
              isOptionEqualToValue={(option: IJob, value: IJob) => option.id === value.id}
              label="Add jobs"
              error={!!errorsMessage.jobs}
              errorStyles={classes.errorMessage}
              dataTestId="job-select"
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
